import React, { useEffect, useState } from 'react';
import './SuggestionCarousel.css';

interface SuggestionCarouselProps {
  prompts: string[];
  onSelect: (prompt: string) => void;
  compact?: boolean;
  autoAdvanceMs?: number;
}

export const SuggestionCarousel: React.FC<SuggestionCarouselProps> = ({
  prompts,
  onSelect,
  compact = false,
  autoAdvanceMs = 4500,
}) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    setIndex(0);
  }, [prompts.length]);

  useEffect(() => {
    if (paused || prompts.length <= 1) return;

    const intervalId = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % prompts.length);
    }, autoAdvanceMs);

    return () => window.clearInterval(intervalId);
  }, [autoAdvanceMs, paused, prompts.length]);

  if (prompts.length === 0) return null;

  const currentPrompt = prompts[index];

  return (
    <div
      className={`suggestion-carousel ${compact ? 'compact' : ''}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div className="suggestion-carousel-track">
        <button
          type="button"
          className="suggestion-carousel-nav"
          aria-label="Previous suggestion"
          onClick={() => setIndex((prev) => (prev - 1 + prompts.length) % prompts.length)}
        >
          <span aria-hidden="true">&lsaquo;</span>
        </button>

        <button
          type="button"
          className="suggestion-carousel-card"
          onClick={() => onSelect(currentPrompt)}
          title={currentPrompt}
        >
          {currentPrompt}
        </button>

        <button
          type="button"
          className="suggestion-carousel-nav"
          aria-label="Next suggestion"
          onClick={() => setIndex((prev) => (prev + 1) % prompts.length)}
        >
          <span aria-hidden="true">&rsaquo;</span>
        </button>
      </div>

      <div className="suggestion-carousel-dots" role="tablist" aria-label="Suggestion carousel pagination">
        {prompts.map((prompt, i) => (
          <button
            key={prompt}
            type="button"
            className={`suggestion-carousel-dot ${i === index ? 'active' : ''}`}
            role="tab"
            aria-label={`Show suggestion ${i + 1}`}
            aria-selected={i === index}
            onClick={() => setIndex(i)}
          />
        ))}
      </div>
    </div>
  );
};
